import { ensureCsrfToken } from './auth';
import { isSessionBlocked } from '@/core';
import { clearSession, createHttpClient, getSession, loadEnv, setSession } from '@/core';
import { z } from 'zod';

const SessionResponseSchema = z.object({
  accessToken: z.string().min(1),
  expiresAt: z.union([z.number(), z.string().datetime({ offset: true })]),
  // CSRF devolvido no corpo do refresh — renovado em memória junto com o token.
  csrf: z.string().min(1),
});

function csrfToken(): string | null {
  return getSession().csrf;
}

/**
 * C4 — composição do httpClient de C2 com a sessão e a config deste app.
 * A troca e a renovação são executadas no BFF com o refresh token em cookie HttpOnly.
 */
async function refreshSession(): Promise<void> {
  if (isSessionBlocked()) throw new Error('Saída pendente. Entre novamente.');
  const env = loadEnv();
  const response = await fetch(`${env.VITE_BFF_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() ?? '' },
  });
  if (!response.ok) {
    clearSession();
    throw new Error('Sessão expirada. Entre novamente.');
  }
  const parsed = SessionResponseSchema.safeParse(await response.json());
  if (!parsed.success) {
    clearSession();
    throw new Error('Resposta de sessão inválida.');
  }
  const expiresAt = typeof parsed.data.expiresAt === 'number'
    ? parsed.data.expiresAt
    : Date.parse(parsed.data.expiresAt);
  setSession({ ...getSession(), accessToken: parsed.data.accessToken, expiresAt, csrf: parsed.data.csrf });
}

export const httpClient = createHttpClient({
  baseUrl: loadEnv().VITE_API_BASE_URL,
  getAccessToken: () => getSession().accessToken,
  refreshSession,
  getCsrfToken: csrfToken,
  canSendRequest: (path) => !isSessionBlocked() || path.startsWith('/auth/password/') || path === '/equipment-categories' || path === '/auth/register',
  ensureCsrfToken: async () => {
    if (getSession().user !== null || getSession().accessToken !== null) await ensureCsrfToken();
  },
  onSessionExpired: () => {
    clearSession();
  },
});
